import MarketExpansionService from "@app/overviews/market-expansion-dashboard/marketExpansionService";
import { MarketExpansionLineOfBusinessEnum } from "@app/overviews/market-expansion-dashboard/shared/MarketExpansionLineOfBusinessEnum";
import { MarketplaceCountyPlanCounts } from "@app/overviews/market-expansion-dashboard/shared/MarketplaceCountyPlanCounts";
import { MedicaidCountyPrograms } from "@app/overviews/market-expansion-dashboard/shared/MedicaidCountyPrograms";
import { MedicareCountyEnrollment } from "@app/overviews/market-expansion-dashboard/shared/MedicareCountyEnrollment";

type MarketOpportunityCountyRecord = MedicareCountyEnrollment | MarketplaceCountyPlanCounts | MedicaidCountyPrograms;

interface MarketOpportunityCountyMetricsParams {
  countySSAs: string[];
  programKeys?: string[];
  /** Whether programKeys is confirmed to belong to the current stateCode (see MarketExpansionFiltersContext). Only meaningful for Medicaid. */
  programSelectionReady?: boolean;
  stateCode: string;
}

interface MarketExpansionLineOfBusinessConfig {
  fetchCountyMetrics: (service: MarketExpansionService, params: MarketOpportunityCountyMetricsParams) => Promise<MarketOpportunityCountyRecord[]>;
  /** Whether the map legend's low-end label shows the smallest county's value instead of always 0. */
  legendUsesMinValue: boolean;
  legendTitle: string;
  /** Label shown in the center of the Market Share pie chart. */
  marketShareTotalLabel: string;
  /** Label shown in the county tooltip's metric line, e.g. "Enrollment: 1,500" or "Plans: 42". */
  metricLabel: string;
  toMetricValue: (record: MarketOpportunityCountyRecord) => number;
  tooltipClassName?: string;
}

const MARKET_EXPANSION_LINE_OF_BUSINESS_CONFIG: Record<MarketExpansionLineOfBusinessEnum, MarketExpansionLineOfBusinessConfig> = {
  [MarketExpansionLineOfBusinessEnum.Medicare]: {
    fetchCountyMetrics: (service, { countySSAs }) =>
      service.searchMedicareCountyEnrollments(countySSAs).then((data) => data ?? []),
    legendTitle: "Membership",
    legendUsesMinValue: false,
    marketShareTotalLabel: "Total Enrollment",
    metricLabel: "Enrollment",
    toMetricValue: (record) => (record as MedicareCountyEnrollment).totalEnrollment,
    tooltipClassName: "membership-popup--medicare"
  },
  [MarketExpansionLineOfBusinessEnum.Medicaid]: {
    fetchCountyMetrics: (service, { stateCode, programKeys, programSelectionReady }) => {
      if (!programSelectionReady) {
        return new Promise<MedicaidCountyPrograms[]>(() => { });
      }
      if (!programKeys || programKeys.length === 0) {
        return Promise.resolve([]);
      }
      return service.searchMedicaidCountyPlanCounts({ programKeys, stateCode }).then((data) => data ?? []);
    },
    legendTitle: "Plans",
    legendUsesMinValue: true,
    marketShareTotalLabel: "Total Plans",
    metricLabel: "Plans",
    toMetricValue: (record) => (record as MedicaidCountyPrograms).planCount,
    tooltipClassName: "membership-popup--medicaid"
  },
  [MarketExpansionLineOfBusinessEnum.Marketplace]: {
    fetchCountyMetrics: (service, { stateCode }) =>
      service.searchMarketplaceCountyPlanCounts(stateCode).then((data) => data ?? []),
    legendTitle: "Plans",
    legendUsesMinValue: true,
    marketShareTotalLabel: "Total Plans",
    metricLabel: "Plans",
    toMetricValue: (record) => (record as MarketplaceCountyPlanCounts).totalPlanCount,
    tooltipClassName: "membership-popup--marketplace"
  },
};

export { MARKET_EXPANSION_LINE_OF_BUSINESS_CONFIG, MarketOpportunityCountyRecord, MarketExpansionLineOfBusinessConfig };
